import {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { navigate } from "@reach/router";
import { Flex, Spinner, Stack } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { Button } from "newui/components/Button";
import { AcceptDeclineModal } from "./components/AcceptDeclineModal/AcceptDeclineModal";
import { Input } from "components/RFQ/Input";
import { getDocumentType } from "actions/RFQ/rfqsAction";
import SkeletonBase from "newui/components/Skeleton";
import { SubmitModal } from "./components/SubmitModal/SubmitModal";
import { useSelector } from "react-redux";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { hasEmptyKeys, showToast } from "utils/helpers";
import {
	getInitialErrors,
	hasDocumentsForCategories,
} from "../../../../pages/RFQSubmission/RFQPreview/RFQUtils/RFQHelper";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import useRefreshWarning from "hooks/useRefreshWarning";
import { hasRfxSubmissionDeadlinePassed } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import { RFX_STATUS, RFX_TABS } from "utils/constants";
import { canEditRfxResponse } from "helpers/rfxHelper";
import { saveSubmission } from "./submission-api";
import SubmissionToolbar from "newui/features/Rfx/Submission/components/SubmissionToolbar/SubmissionToolbar";
import { isRFxClosed, isRfxStatusMatch } from "newui/features/Rfx/utils";
import { downloadAllDocumentation } from "newui/features/Rfx/api/rfx";
import { RFX_SUBMISSION_STATUS } from "./constants";
import { getSubmissionDataModel, initialRfxRequestData } from "./submission-helpers";
import {
	initialiseQuoteValues,
	initialiseReturnableSchedule,
} from "./data-processor";
import { useRfxTabMenu } from "./components/ViewSubmission/useRfxTabMenu";
import {
	RfxDetails,
	SubmissionDetails,
	ViewSubmission,
} from "./components/ViewSubmission";
import { rfxSubmission } from ".";
import { useRfxSubmissionData } from "../data-provider/RfxSubmissionDataProvider";
import TabComponent from "newui/components/TabComponent";
import { useCurrentCompanyId, useCurrentCompanyValue, useCurrentUser, useCurrentUserId } from "hooks/auth";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { getPendingRevisionRequest, submitRevision } from "./api/revision-api";
import { useRevisionNotifications } from "./hooks/useRevisionNotifications";
import { RequestRevisionDrawer, RevisionRequestDialog } from "./components/RevisionRequest";
/*
  name : content request name,
  type: type of content either custom form or file,
  templateFile: file uploaded by procurer,
  uploadedFile: file uploaded by supplier in response to template file,
  questions: custom form question by procurer,
  answers: answer given supplier
*/



/**
 * This is the view that the Supplier gets and the 'View RFx Details' view for the Client
 * @param {*} param0
 * @returns
 */
const RFQPreview = ({ currentTab, contextId }) => {
	const tabHeaderRef = useRef(null);
	const formRef = useRef(null);
	const quoteTabRef = useRef(null);

	// form initial values and form current status
	const [initialFormValue, setInitialFormValue] = useState(null);
	const [validForm, setValidForm] = useState(true);
	const [errors, setErrors] = useState(null);

	// loading status
	const [loadingSave, setLoadingSave] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const { guestPaths, supplierPaths } = useAuthContext();

	const { uploadInProgresses } = useContext(RfxContext);

	const {
		currentTab: submissionTab,
		hasRfxSubmissionLoaded,
		hasSubmitted,
		isBeingRevised,
		isClientView,
		isStatusProgressOrAccepted,
		rfxData,
		rfxId,
		rfxSubmissionId,
		rfxSubmissionData,
		rfxTabTotals,
		reloadRfxSubmissionData,
		updateLocalRfxSubmissionData,
	} = useRfxSubmissionData();

	// rfx status
	const rfxClosedStatus = isRFxClosed(rfxData?.status ?? "");
	const hasDeadlinePassed = hasRfxSubmissionDeadlinePassed(rfxData);

	//
	const [documentCategories, setDocumentCategories] = useState([]);
	const [documentFilter, setDocumentFilter] = useState(null);

	useRefreshWarning(
		uploadInProgresses?.rfxBidder?.length > 0,
		"Are you sure you want to refresh the page?"
	);

	const {
		documents,
		isRequesting,
		requestingResources,
		loadingPastProjects,
	} = useSelector((state) => state?.companyProfile);

	// current user profile type either guest or bidder with account
	const { guestUserEmail, isGuest } = useCurrentUser();
	const isLoadedCompanyProfile = isGuest
		? true
		: !isRequesting && !requestingResources && !loadingPastProjects;

	const showIsLoading =
		isLoading || !hasRfxSubmissionLoaded || !isLoadedCompanyProfile;

	// rfx form data
	const [rfxRequestData, setRfxRequestData] = useState(initialRfxRequestData);
	const requestRfxReturnables = rfxRequestData.returnableSchedules;
	const requestRfxQuote = rfxRequestData.quotes;
	const canEditRfx = canEditRfxResponse(
		showIsLoading,
		rfxData,
		rfxSubmissionData?.status
	);

	// filter company resources to select appropriate form field type either smart field or textarea
	const [hasDocuments, setHasDocuments] = useState(false);

	let currentNavigationTab = submissionTab ?? currentTab;

	if (!currentNavigationTab) {
		currentNavigationTab = hasDocuments
			? RFX_TABS.DOCUMENTATION
			: RFX_TABS.QUESTIONNAIRE;
	}

	// currentCompany
	const currentCompanyId = useCurrentCompanyId();
	let companyHasKeyPersonnel = useCurrentCompanyValue('has_past_projects');
	let companyHasPastProjects = useCurrentCompanyValue('has_past_projects');

	const companyId =
		rfxSubmissionData?.company_id ?? currentCompanyId;

	// handle status for dialog
	const [showSubmitDialog, setShowSubmitDialog] = useState(false);
	const [showDialog, setShowDialog] = useState(null);
	const [isNotFound, setIsNotFound] = useState(false);

	// revision state
	const [showRevisionDrawer, setShowRevisionDrawer] = useState(false);
	const [showRevisionDialog, setShowRevisionDialog] = useState(false);
	const [revisionRequest, setRevisionRequest] = useState(null);
	const [hasDraftRevision, setHasDraftRevision] = useState(false);

	const currentUserId = useCurrentUserId();
	const rfxInviteId = rfxSubmissionData?.id;

	const onQuoteRevisionUpdated = (isQuoteTabValid) => {
		if (isQuoteTabValid !== undefined) {
			updateLocalRfxSubmissionData({
				...rfxSubmissionData,
				isQuoteTabValid,
			});
		}
		refreshRevisionRequest();
	}

	const refreshRevisionRequest = useCallback(async () => {
		if (!rfxInviteId || !isRfxStatusMatch(rfxData?.status, RFX_STATUS.EVALUATION) || !rfxData?.rfxType?.show_quote || (rfxSubmissionData?.status !== RFX_SUBMISSION_STATUS.SUBMITTED && rfxSubmissionData?.status !== RFX_SUBMISSION_STATUS.BEING_REVISED)) {
			return;
		}
		try {
			const request = await getPendingRevisionRequest(rfxInviteId);
			setRevisionRequest(request);
		} catch {
			// Error handled by global interceptor
		}
	}, [rfxData?.rfxType?.show_quote, rfxInviteId, rfxSubmissionData?.status, rfxData?.status]);

	const openSubmitRevisionDialog = () => {
		setShowSubmitDialog(true);
	}

	const handleSubmitRevision = useCallback(async () => {
		if (!rfxInviteId) {
			return;
		}

		try {
			await submitRevision(rfxInviteId);
			setInitialFormValue(null);
			reloadRfxSubmissionData();
			refreshRevisionRequest();
			showToast("Your revised quote has been submitted.", "Revision Submitted", true);
		} catch {
			showToast("Failed to submit revision.", "Error");
		}
	}, [rfxInviteId, reloadRfxSubmissionData, refreshRevisionRequest]);

	useEffect(() => {
		refreshRevisionRequest();
	}, [refreshRevisionRequest]);

	useRevisionNotifications({
		userId: currentUserId,
		rfxInviteId: rfxInviteId,
		onRevisionRequested: () => {
			refreshRevisionRequest();
			reloadRfxSubmissionData();
		},
		onRevisionApproved: () => {
			refreshRevisionRequest();
			reloadRfxSubmissionData();
			showToast("Your revision request has been approved.", "Revision Approved", true);
		},
		onRevisionDeclined: () => {
			refreshRevisionRequest();
			showToast("Your revision request has been declined.", "Revision Declined");
		},
		onRevisionSubmitted: () => {
			refreshRevisionRequest();
			reloadRfxSubmissionData();
			showToast("The supplier has submitted a revised quote.", "Revision Submitted", true);
		},
	});
	const { setSaveHandler } = useContext(RfxContext);
	// tab contents are filtered based on tab names
	const { tabMenu } = useRfxTabMenu(rfxData, rfxTabTotals, { isClientView: isClientView });

	const focusErrorField = useCallback(() => {
		if (canEditRfx) {
			formRef?.current?.validateForm().then((res) => {
				setErrors(res);
				formRef?.current?.setTouched(res);
			});
		}
	}, [canEditRfx, formRef]);

	useEffect(() => {
		// tabHeaderRef?.current?.scrollIntoView({ behavior: "instant" });
		focusErrorField();
	}, [currentNavigationTab, focusErrorField]);

	const loadRfxSubmissionData = (refreshData = false) => {
		if (refreshData) {
			setInitialFormValue(null);
			reloadRfxSubmissionData();
			return;
		}

		setIsLoading(true);

		try {
			if (!documentCategories) {
				getDocumentType().then((res) => {
					if (res) {
						setDocumentCategories([
							{ value: null, label: "All Categories" },
							...res.map((category) => ({
								value: category?.id,
								label: category?.name,
							})),
						]);
					}
				});
			}
			setRfxDisplayData();
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (isLoadedCompanyProfile && hasRfxSubmissionLoaded && rfxData) {
			loadRfxSubmissionData(false);
		}

	}, [isLoadedCompanyProfile, hasRfxSubmissionLoaded, rfxData]);

	const setRfxDisplayData = () => {
		const status = rfxSubmissionData?.status;
		const submission = rfxSubmissionData?.submission;

		const { quotes, quoteType, questionnaire, returnableScheduleType } =
			rfxData;

		if (status?.toLowerCase() === "decline") {
			isGuest ? navigate(".") : navigate(`/account/${companyId}/rfx`);
		} else {
			if (isLoadedCompanyProfile) {
				const hasMatchingDocuments = questionnaire?.some((section) =>
					section.questionnaire?.some(
						(question) =>
							question?.fields?.type === "corporate_documents" &&
							hasDocumentsForCategories(question, documents)
					)
				);

				setHasDocuments(hasMatchingDocuments);

				const hasGotKeyPersonnel = rfxSubmissionData?.has_key_personnel ?? companyHasKeyPersonnel;
				const hasGotPastProjects = rfxSubmissionData?.has_past_projects ?? companyHasPastProjects;

				const initialReturnable =
					returnableScheduleType !== "file"
						? initialiseReturnableSchedule(
							questionnaire,
							submission?.rfxAnswers,
							hasMatchingDocuments,
							hasGotPastProjects,
							hasGotKeyPersonnel,
							isClientView
						)
						: {
							returnable_schedule_file_responses:
								submission?.returnable_schedule_file_responses || null,
						};

				const initialQuote =
					quoteType === "file"
						? { quote_files: submission?.quote_files }
						: initialiseQuoteValues(quotes, submission?.rfxQuoteAnswers, submission?.rfxQuoteCells);

				const initialFormValue = {
					...initialReturnable,
					...initialQuote,
					returnables_type: returnableScheduleType,
					quote_type: quoteType,
					flexible_quote_files: submission?.flexible_quote_files || null,
				};

				setInitialFormValue(initialFormValue);

				setRfxRequestData(getSubmissionDataModel(rfxData, rfxSubmissionData, hasGotKeyPersonnel, hasGotPastProjects));
			}
		}
	};

	useEffect(() => {
		// check for initial errors
		if (isStatusProgressOrAccepted) {
			const initialErrors = getInitialErrors(
				requestRfxReturnables?.questions,
				requestRfxQuote?.questions,
				requestRfxQuote,
				requestRfxReturnables
			);
			focusErrorField();
			setValidForm(hasEmptyKeys(initialErrors));
		}
	}, [
		focusErrorField,
		rfxRequestData,
		isStatusProgressOrAccepted,
		requestRfxReturnables,
		requestRfxQuote,
	]);

	const downloadAllHandler = () => {
		setDownloadLoading(true);
		downloadAllDocumentation(rfxData).finally(() => {
			setDownloadLoading(false);
		});
	};

	// const autoSaveHandler = () => {
	// 	saveHandler(RFX_TABS.QUESTIONNAIRE, null, false);
	// };

	const isSubmissionDirty = (newValues, initialValues) => {
		return true;
		// return Object.keys(newValues).some((key) => {
		// 	// if (IGNORED_FIELDS.includes(key)){ return false;}
		// 	return newValues[key] !== initialValues[key];
		// });
	};

	const saveHandler = useCallback(
		async (currentTab, nextTab, isSubmitting = false) => {
			const newValues = formRef?.current?.values;
			// const oldValues = lastSaveValue ?? initialFormValue;
			// const isDirty = isSubmissionDirty(newValues, oldValues);

			const shouldAutoSave =
				!hasSubmitted &&
				canEditRfx &&
				currentTab !== RFX_TABS.COMMS &&
				currentTab !== RFX_TABS.ADDENDUMS;

			if (!shouldAutoSave) {
				// if (!shouldAutoSave || !isDirty)
				return;
			}

			// Validate and save QuoteTab (supplier rows + schedule of rates)
			// Validation sets visual indicators but does not block save
			let quoteValid = true;
			if (quoteTabRef.current) {
				quoteValid = await quoteTabRef.current.validateAndSave();
			}

			const hasQuote = rfxData?.rfxType?.show_quote;
			const isFormQuote = hasQuote && rfxData?.quoteType !== "file";
			if (isFormQuote) {
				updateLocalRfxSubmissionData({
					...rfxSubmissionData,
					isQuoteTabValid: quoteValid,
				});
			}

			focusErrorField();

			return new Promise((resolve, reject) => {
				const payload = rfxSubmission.generateSaveSubmissionPayload(
					formRef?.current?.values,
					requestRfxReturnables?.questions,
					isGuest
				);
				saveSubmission(rfxSubmissionData.id, payload, guestUserEmail)
					.then((response) => {
						if (response) {
							!isSubmitting && showToast(response.message, "Success", true);
							//setRfxRequestData(mapAnswersToQuestions(rfxRequestData, payload));
							//formRef.current.resetForm({ newValues });

							resolve(true);
						} else {
							reject(false);
						}
					})
					.catch((e) => {
						reject(false);
					});
			}).catch((exception) => {
				exception.message &&
					showToast(exception.message, "Failed To Save", false);
			});
		},
		[
			focusErrorField,
			formRef,
			requestRfxReturnables?.questions,
			isGuest,
			guestUserEmail,
			canEditRfx,
			hasSubmitted,
			rfxData,
			rfxSubmissionData,
			updateLocalRfxSubmissionData,
		]
	);

	const onSave = useCallback(
		async (currentTab, nextTab, isSubmitting = false) => {
			setLoadingSave(true);
			try {
				return await saveHandler(currentTab, nextTab, isSubmitting);
			} finally {
				// guaranteed to run AFTER saveHandler finishes
				!isSubmitting && setLoadingSave(false);
			}
		},
		[saveHandler]
	);

	useEffect(() => {
		setSaveHandler(() => onSave);
	}, [onSave, setSaveHandler]);

	const submitHandler = useCallback(async () => {
		try {
			if (formRef?.current?.isValid) {
				await formRef?.current?.submitForm();
				setShowSubmitDialog(true);
			}
		} catch (e) {
			// TODO: what should we do here?
		}

	}, [formRef]);

	const revokeSubmissionHandler = async () => {
		setShowSubmitDialog(true);
	};

	const afterRevokeSubmissionHandler = (result) => {
		// update submission status
		result && alterSubmissionStatus(RFX_SUBMISSION_STATUS.INPROGRESS);
	};

	const afterSubmissionHandler = (submissionDate) => {
		if (!submissionDate) {
			setLoadingSave(false);
			return;
		}

		updateLocalRfxSubmissionData({
			...rfxSubmissionData,
			status: RFX_SUBMISSION_STATUS.SUBMITTED,
			submittedAt: submissionDate,
		});
		setLoadingSave(false);
	};

	const alterSubmissionStatus = (newStatus) => {
		updateLocalRfxSubmissionData({ ...rfxSubmissionData, status: newStatus });
	};

	const continueTab = async () => {
		let current = tabMenu.findIndex((tab) => tab.id === currentNavigationTab);
		let next = tabMenu[current + 1].id;
		if (current === 1 || current === 2) {
			onSave(currentNavigationTab, next);
		}

		const navigateTo =
			supplierPaths?.viewRfxSubmission(rfxId, rfxSubmissionId, next) ??
			guestPaths?.viewRfxSubmission(rfxId, next);

		navigate(navigateTo);
	};

	// _ is used due to the value of first parameter is unused
	const changeTab = (_, id) => {
		const navigateTo =
			supplierPaths?.viewRfxSubmission(rfxId, rfxSubmissionId, id) ??
			guestPaths?.viewRfxSubmission(rfxId, id);

		navigate(navigateTo);
		onSave(currentNavigationTab, id);
	};

	const rfxHasQuote = rfxData?.rfxType?.show_quote;
	const isQuoteTabValid = rfxSubmissionData?.isQuoteTabValid ?? true;
	const needsQuoteValidation = rfxHasQuote && rfxData?.quoteType !== "file";
	const quoteTabReady = !needsQuoteValidation || isQuoteTabValid;

	const buttonActionProps = {
		continueTab: continueTab,
		currentTab: currentNavigationTab,
		hasDeadlinePassed: hasDeadlinePassed,
		isFileUploadInProgress: uploadInProgresses?.rfxBidder?.length > 0,
		isClientView: isClientView,
		isGuest: isGuest,
		isLoading: showIsLoading,
		isRFxClosed: rfxClosedStatus,
		isQuoteTabValid: quoteTabReady,
		loadingSave: loadingSave,
		rfxData: rfxData,
		revokeSubmissionHandler: revokeSubmissionHandler,
		saveHandler: onSave,
		submissionId: rfxSubmissionData?.id,
		submissionStatus: rfxSubmissionData?.status,
		submitHandler: submitHandler,
		validForm: validForm,
		setShowDialog: setShowDialog,
		onRequestRevision: () => setShowRevisionDrawer(true),
		onSubmitRevision: openSubmitRevisionDialog,
		hasDraftRevision: hasDraftRevision,
		pendingRevisionRequest: revisionRequest,
	};

	const viewSubmissionProps = {
		currentTab: currentNavigationTab,
		documentFilter: documentFilter,
		errors: errors,
		formRef: formRef,
		guestEmail: guestUserEmail,
		hasDocuments: hasDocuments,
		hasSubmitted: hasSubmitted,
		isClientView: isClientView,
		isGuest: isGuest,
		initialFormValue: initialFormValue,
		isLoading: showIsLoading,
		isReadOnlyMode: !canEditRfx || hasSubmitted,
		submissionData: rfxRequestData,
		submissionStatus: rfxSubmissionData?.status,
		rfxInviteStatus: rfxSubmissionData?.status,
		rfxInviteId: rfxSubmissionData?.id,
		onQuoteRevisionUpdated: onQuoteRevisionUpdated,
		onDraftRevisionChange: setHasDraftRevision,
		quoteTabRef: quoteTabRef,
		validForm: validForm,
		setErrors: setErrors,
		setValidForm: setValidForm,
	};

	if (isNotFound) {
		return <NotFound />;
	}

	return (
		<div>
			<SubmissionToolbar {...buttonActionProps} />
			<div
				id="layout-dashboard"
				className="mt-8"
			>
				{isClientView ? (
					<SubmissionDetails
						isClientView={isClientView}
						isLoading={showIsLoading}
						onReload={() => loadRfxSubmissionData(true)}
						pendingRevisionRequest={revisionRequest}
						onViewRevisionRequest={() => setShowRevisionDialog(true)}
					/>
				) : (
					<RfxDetails
						rfxData={rfxData}
						isLoading={showIsLoading}
						submissionStatus={rfxSubmissionData?.status}
						submissionStatusDate={rfxSubmissionData?.submittedAt}
						onReload={() => loadRfxSubmissionData(true)}
						onRequestRevision={() => setShowRevisionDrawer(true)}
					/>
				)}
				<Flex
					direction={{ base: "column", md: "row" }} // stack on mobile, row on desktop
					w="full"
					gap={2}
					align={{ base: "stretch", md: "center" }}
					className="pt-5"
				>
					<TabComponent
						menus={tabMenu}
						activeTab={currentNavigationTab}
						changeActiveTab={changeTab}
						isLoading={showIsLoading}
					/>

					<div
						ref={tabHeaderRef}
						id="rfx-container"
					></div>
					{!showIsLoading ? (
						<Flex
							align={"center"}
							style={{ gap: "8px" }}
						>
							{rfxData?.documentationDocument?.length > 0 &&
								currentNavigationTab === RFX_TABS.DOCUMENTATION && (
									<Stack
										direction={{
											base: "column",
											sm: "row",
										}}
										wrap={{ base: "wrap", lg: "nowrap" }}
										w={"fit-content"}
										gap={2}
										align={{ base: "stretch", md: "center" }}
										className="pb-4"
									>
										<Input
											type="select"
											name="category"
											placeholder="Refine by Category"
											onChange={(e) => setDocumentFilter(e.value)}
											value={documentFilter}
											options={documentCategories}
											className="min-w-[280px]"
										/>
										<Button
											btntype="secondary"
											className="!h-[56px] !min-w-[180px]"
											disabled={downloadLoading}
											onClick={downloadAllHandler}
											data-testid="download-all-documents"
											width={{ base: "full", md: "180px" }}
										>
											{!downloadLoading ? (
												<Flex
													align={"center"}
													gap={2}
												>
													<Icon
														icon="download"
														color="#0031DD"
													/>
													Download All
												</Flex>
											) : (
												<Spinner size="md" />
											)}
										</Button>
									</Stack>
								)}
						</Flex>
					) : (
						<Flex gap={2}>
							<SkeletonBase
								width="240px"
								height="56px"
							/>
							<SkeletonBase
								width="160px"
								height="56px"
							/>
						</Flex>
					)}
				</Flex>
				{showIsLoading ? (
					<>
						<SkeletonBase
							startColor="lightgray"
							width="100%"
							height="72px"
							className="my-4 !rounded-lg"
						/>
						<SkeletonBase
							startColor="lightgray"
							width="100%"
							height="72px"
							className="!rounded-lg"
						/>
					</>
				) : (
					<>
						<ViewSubmission {...viewSubmissionProps} />
					</>
				)}

				<SubmissionToolbar
					isBottom
					{...buttonActionProps}
				/>

				<AcceptDeclineModal
					showDialog={showDialog}
					onClose={() => setShowDialog(null)}
					rfxId={rfxId}
					setSubmissionStatus={alterSubmissionStatus}
					guestEmail={guestUserEmail}
					isGuest={isGuest}
					companyId={companyId}
				/>

				<SubmitModal
					afterSubmissionHandler={afterSubmissionHandler}
					afterRevokeSubmissionHandler={afterRevokeSubmissionHandler}
					showDialog={showSubmitDialog}
					onClose={() => setShowSubmitDialog(false)}
					submissionId={rfxSubmissionData?.id}
					companyId={parseInt(companyId?.toString())}
					saveSubmissionHandler={onSave}
					guestEmail={guestUserEmail}
					hasSubmitted={hasSubmitted}
					isGuest={isGuest}
					onBeingRevisedSubmit={handleSubmitRevision}
				/>

				{showRevisionDrawer && rfxData && rfxSubmissionData && (
					<RequestRevisionDrawer
						isOpen={showRevisionDrawer}
						onClose={() => setShowRevisionDrawer(false)}
						rfxData={rfxData}
						rfxInviteData={rfxSubmissionData}
						onSuccess={() => {
							refreshRevisionRequest();
							setShowRevisionDrawer(false);
						}}
					/>
				)}

				{showRevisionDialog && revisionRequest && (
					<RevisionRequestDialog
						isOpen={showRevisionDialog}
						onClose={() => setShowRevisionDialog(false)}
						revisionRequest={revisionRequest}
						responseFromCompanyName={rfxSubmissionData?.company?.name}
						onActionComplete={() => {
							refreshRevisionRequest();
							reloadRfxSubmissionData();
							setShowRevisionDialog(false);
						}}
					/>
				)}

			</div>
		</div>
	);
};

export default RFQPreview;
